import type { PlanCard } from '../types';
import type { PlansResult } from '../data/plans';
import { dateLong } from '../lib/format';
import { CareIcon } from '../icons';
import { segments } from '../lib/progress';

/* ПЛАНЫ УХОДА. Единственный раздел кабинета, у которого ЕСТЬ настоящая ручка:
   `GET post-care-api/read/patient/post-care/channel`. Контракт писался прямо под
   этот экран — в его докстроке так и сказано, «строка меню кабинета».

   ЗАВЕРШЁННОСТЬ БЕРЁТСЯ У СЕРВЕРА (`completed`), а не считается тут по
   `checksDone === checksTotal`: у плана без пунктов такое сравнение дало бы
   «завершён». Сервер называет это предикатом, и снятие отметки возвращает план в
   незавершённые — то есть значение живое, а не однажды записанное.

   ШКАЛА ЭТАПОВ живёт в `lib/progress` — одна на этот список и на страницу
   одного плана. Считается она по отмеченным пунктам и не длиннее десяти полосок;
   почему так, написано там же.

   ПОДПИСЬ СНИЗУ ОТВЕЧАЕТ НА «ЧТО ДЕЛАТЬ», и у неё три случая: план завершён,
   есть ближайший день с делами (`nextDay`), процедура ещё впереди
   (`dayNumber < 0` — визит перенесли вперёд, и шкала ещё не началась). */

export function Plans({ result, onOpen }: { result: PlansResult; onOpen: (id: string) => void }) {

    return (
        <>
            <div className="mh">
                <div>
                    <h1>Планы ухода</h1>
                    <div className="sub">что делать после процедуры, по дням и этапам</div>
                </div>
            </div>

            <div className="cols">
                <div>{body()}</div>
            </div>
        </>
    );

    function body() {
        if (result.state === 'offline') {
            return <div className="empty">Планы ухода не подключены в этом окружении.</div>;
        }
        if (result.state === 'forbidden') {
            return (
                <div className="empty">
                    Планы ухода пока недоступны для вашего аккаунта. Это настройка доступа на сервере —
                    повторная попытка не поможет.
                </div>
            );
        }
        if (result.state === 'error') {
            return <div className="empty">Не удалось загрузить планы ухода. Обновите страницу.</div>;
        }

        const active = result.plans.filter((p) => !p.completed);
        const done = result.plans.filter((p) => p.completed);

        if (result.plans.length === 0) {
            return (
                <div className="empty">
                    Планов ухода пока нет. Врач присылает план после процедуры — он появится здесь сам.
                </div>
            );
        }

        return (
            <>
                {active.length > 0 && (
                    <>
                        <div className="sec">
                            <h2>{active.length === 1 ? 'Активный план' : 'Активные планы'}</h2>
                        </div>
                        <div className="list">
                            {active.map((p) => (
                                <Card key={p.id} p={p} onOpen={onOpen} />
                            ))}
                        </div>
                    </>
                )}
                {done.length > 0 && (
                    <>
                        <div className={active.length > 0 ? 'sec gap' : 'sec'}>
                            <h2>Завершённые</h2>
                            <span className="n">{plural(done.length)}</span>
                        </div>
                        <div className="list">
                            {done.map((p) => (
                                <Card key={p.id} p={p} onOpen={onOpen} />
                            ))}
                        </div>
                    </>
                )}
            </>
        );
    }
}

function Card({ p, onOpen }: { p: PlanCard; onOpen: (id: string) => void }) {
    // Правило шкалы общее со страницей плана (`lib/progress`): там оно раньше
    // было СВОЁ, считалось по календарю, и два экрана рисовали разное.
    const { total, filled } = segments(p.checksDone, p.checksTotal);

    return (
        <button
            type="button"
            className={p.completed ? 'p fin' : 'p act'}
            onClick={() => onOpen(p.id)}
        >
            <span className="ph">
                <span className="pi">
                    <CareIcon />
                </span>
                <span className="ptx">
                    <b>{p.title}</b>
                    <span>{[p.doctor.name, p.doctor.clinicName].filter(Boolean).join(', ')}</span>
                </span>
                <span className="pcnt">
                    <b>
                        {p.checksDone} из {p.checksTotal}
                    </b>
                    <i>{p.checksTotal === 1 ? 'этап' : 'этапов'}</i>
                </span>
            </span>
            <span className="seg">
                {Array.from({ length: total }, (_, i) => (
                    <i key={i} className={i < filled ? 'f' : undefined} />
                ))}
            </span>
            <span className="pfoot">
                <span className="u">{note(p)}</span>
                <span className="go">{p.completed ? 'посмотреть' : 'открыть план'}</span>
            </span>
        </button>
    );
}

function note(p: PlanCard): string {
    if (p.completed) return `Завершён, процедура ${dateLong(p.anchorAt)}`;
    // Отрицательный день законен: визит перенесли вперёд, процедура ещё не была.
    if (p.dayNumber < 0) return `Процедура ${dateLong(p.anchorAt)}, план начнётся после неё`;
    if (p.nextDay !== null) return `Ближайшие дела — день ${p.nextDay} плана`;
    return `Идёт день ${p.dayNumber} из ${p.dayTotal}`;
}

function plural(n: number): string {
    const t = n % 100;
    const d = n % 10;
    if (t > 10 && t < 20) return `${n} планов`;
    if (d === 1) return `${n} план`;
    if (d >= 2 && d <= 4) return `${n} плана`;
    return `${n} планов`;
}
